import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { MapContainer, TileLayer, Marker, Tooltip as LTooltip, Popup, Polyline } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { busIcon, pulseBusIcon, depotIcon, TILE_URL, TILE_ATTR, STATUS_COLOR, DEPOTS } from "@/lib/mapIcons";
import { BreakdownPopup, BreakdownAlertPanel, MobileBreakdownSheet } from "@/components/BreakdownWidgets";
import { Bus, MapPin, Clock, Phone, ArrowsClockwise, Path } from "@phosphor-icons/react";

const STATUS_COLOR_LM = STATUS_COLOR;

// Depot palette (matches NetworkMap.jsx so admin sees the same colours)
const DEPOT_POLY_COLORS = {
  "Panaji":   "#1E3A8A",
  "Mapusa":   "#DC2626",
  "Margao":   "#F59E0B",
  "Vasco":    "#10B981",
  "Ponda":    "#8B5CF6",
  "Canacona": "#EC4899",
};

export default function LiveMap() {
  const depotMap = useDepots();
  const [buses, setBuses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("All");
  const [mobileBreakdownBus, setMobileBreakdownBus] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [polylines, setPolylines] = useState([]);
  const [showRoutes, setShowRoutes] = useState(true);
  const [routesLoading, setRoutesLoading] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const load = async () => {
    const { data } = await api.get("/fleet/buses");
    setBuses(data);
    if (!selected && data.length) setSelected(data[0]);
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  // One-time load of route polylines as backdrop layer (GTFS data, ~476 routes)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRoutesLoading(true);
      try {
        // Pull all routes (476) so every depot is represented in the overlay,
        // not just Panaji which dominates the first 100 alphabetically.
        const { data } = await api.get("/network/polylines?limit=500");
        if (!cancelled) setPolylines(data || []);
      } catch (e) {
        if (!cancelled) setPolylines([]);
      } finally {
        if (!cancelled) setRoutesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = buses.filter(b =>
    (statusF === "All" || b.status === statusF) &&
    (q === "" || b.fleet_id.toLowerCase().includes(q.toLowerCase()) || b.registration_number.toLowerCase().includes(q.toLowerCase()))
  );

  const counts = buses.reduce((m, b) => { m[b.status] = (m[b.status]||0)+1; return m; }, {});
  const highlightedDepot = selected?.depot || null;

  // Memoize the polyline layer — 476 paths shouldn't re-render on every
  // bus tick (which arrives every 10s).
  const polylineLayer = useMemo(() => {
    if (!showRoutes || polylines.length === 0) return null;
    return polylines.map((p) => {
      const color = DEPOT_POLY_COLORS[p.depot] || "#64748B";
      const isHi = highlightedDepot && p.depot === highlightedDepot;
      return (
        <Polyline
          key={`poly-${p.route_id}`}
          positions={p.polyline}
          pathOptions={{
            color,
            weight: isHi ? 4 : 2,
            opacity: isHi ? 0.85 : 0.30,
            lineCap: "round",
            lineJoin: "round",
          }}>
          <LTooltip sticky direction="top" offset={[0, -2]}>
            <div className="text-xs">
              <div className="font-extrabold text-[#1E3A8A]">{p.route_id}</div>
              <div className="font-semibold">{p.name}</div>
              <div className="text-slate-500 mt-0.5">{fmtDepot(p.depot, depotMap)} · {p.category} · {p.stops} stops</div>
            </div>
          </LTooltip>
        </Polyline>
      );
    });
  }, [polylines, showRoutes, highlightedDepot]);

  return (
    <div className="flex flex-col lg:flex-row h-full">
      <div className="flex-1 flex flex-col bg-white min-h-[50vh] lg:min-h-0">
        <div className="px-4 lg:px-5 py-3 lg:py-4 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="label-cap text-slate-500">Tracking</div>
            <h1 className="font-display text-lg lg:text-2xl font-bold leading-tight">Live Map &amp; Tracking</h1>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="status-dot status-Available"></span>
            <span className="text-slate-600">Auto Refresh · 10s</span>
            <button
              onClick={() => setShowRoutes((v) => !v)}
              data-testid="toggle-routes-overlay"
              className={`ml-2 flex items-center gap-1 px-2 py-1 border rounded text-[11px] font-bold transition-colors
                ${showRoutes
                  ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              title="Toggle GTFS route polylines overlay">
              <Path size={12} weight="bold"/>
              {showRoutes ? "Routes ON" : "Routes OFF"}
              {routesLoading && <span className="ml-1 opacity-60">…</span>}
              {!routesLoading && polylines.length > 0 && (
                <span className="opacity-75">· {polylines.length}</span>
              )}
            </button>
            <button onClick={load} className="ml-1 p-1.5 border border-slate-200 rounded hover:bg-slate-50"><ArrowsClockwise size={14}/></button>
          </div>
        </div>

        {/* Mobile-only horizontal status strip — replaces the floating Vehicle Status legend */}
        <div className="lg:hidden px-3 py-2 border-b border-slate-100 bg-slate-50 overflow-x-auto scroll-thin">
          <div className="flex items-center gap-2 w-max">
            <span className="label-cap text-slate-500 shrink-0 pr-1">Status</span>
            {Object.entries(STATUS_COLOR).map(([s, c]) => (
              <span key={s} className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-2 py-1 text-[11px]">
                <span className="w-1.5 h-1.5 rounded-full" style={{background:c}}/>
                <span className="text-slate-700">{s}</span>
                <span className="tabular-nums font-bold text-[#1E3A8A]">{counts[s]||0}</span>
              </span>
            ))}
            <span className="shrink-0 inline-flex items-center gap-1.5 bg-[#1E3A8A] text-white rounded-full px-2 py-1 text-[11px] font-semibold">
              Total <span className="tabular-nums">{buses.length}</span>
            </span>
          </div>
        </div>

        <div className="flex-1 relative">
          <MapContainer center={[15.39, 73.99]} zoom={10} style={{ height: "100%", width: "100%" }}>
            <TileLayer url={TILE_URL} attribution={TILE_ATTR}/>
            {/* GTFS route polylines backdrop — colored by depot.
                Selected bus's depot routes are highlighted in higher opacity. */}
            {polylineLayer}
            {DEPOTS.map(d => {
              const fleet = buses.filter(b => b.depot === d.name);
              const stats = fleet.reduce((m,b)=>{m[b.status]=(m[b.status]||0)+1;return m;},{});
              return (
                <Marker key={d.name} position={[d.lat, d.lng]} icon={depotIcon()}>
                  <Popup>
                    <div className="text-xs space-y-1 min-w-[180px]">
                      <div className="font-display font-bold text-sm text-[#0F1A3D]">{d.name} Depot</div>
                      <div className="text-slate-600 border-b border-slate-100 pb-1">Total fleet: <b>{fleet.length}</b></div>
                      {Object.entries(stats).map(([s,n])=>(
                        <div key={s} className="flex justify-between"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{background:STATUS_COLOR[s]}}/>{s}</span><b>{n}</b></div>
                      ))}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            <MarkerClusterGroup chunkedLoading disableClusteringAtZoom={12} maxClusterRadius={50}>
              {filtered.filter(b=>b.status!=="Breakdown").map((b) => (
                <Marker key={b.id} position={[b.lat, b.lng]} icon={busIcon(b.status, selected?.id===b.id ? 36 : 28)}
                  eventHandlers={{ click: () => setSelected(b) }}>
                  <Popup>
                    <div className="text-xs space-y-1 min-w-[180px]">
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-[#1E3A8A] text-sm">{b.fleet_id}</span>
                        <span className="px-1.5 py-0.5 rounded text-white text-[10px]" style={{background: STATUS_COLOR[b.status]}}>{b.status}</span>
                      </div>
                      <div className="text-slate-700 font-semibold">{b.registration_number}</div>
                      <div className="text-slate-500">{b.category} · {fmtDepot(b.depot, depotMap)}</div>
                      <div className="grid grid-cols-3 gap-2 pt-1 border-t border-slate-100">
                        <div><div className="text-[9px] uppercase text-slate-400">SOC</div><div className="font-bold">{b.soc}%</div></div>
                        <div><div className="text-[9px] uppercase text-slate-400">Speed</div><div className="font-bold">{b.speed}<span className="font-normal text-[9px]"> km/h</span></div></div>
                        <div><div className="text-[9px] uppercase text-slate-400">Delay</div><div className="font-bold" style={{color: b.delay_min>5?"#F97316":"#22C55E"}}>{b.delay_min>0?`+${b.delay_min}m`:"OK"}</div></div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
            {/* Breakdown buses rendered OUTSIDE cluster — always visible.
                On mobile, opening a Leaflet popup is broken (no touch scroll), so we
                bypass the popup and open a proper bottom-sheet instead. */}
            {filtered.filter(b=>b.status==="Breakdown").map((b) => (
              <Marker key={b.id} position={[b.lat, b.lng]} icon={pulseBusIcon(b.status, 38)}
                      eventHandlers={{ click: () => { setSelected(b); if (isMobile) setMobileBreakdownBus(b); } }}
                      zIndexOffset={1000}>
                {!isMobile && <BreakdownPopup bus={b}/>}
              </Marker>
            ))}
          </MapContainer>
          <BreakdownAlertPanel buses={buses} onSelect={(b)=>{ setSelected(b); if (isMobile) setMobileBreakdownBus(b); }}/>
          <div className="hidden lg:block absolute right-4 bottom-4 z-[400] bg-white border border-slate-200 rounded-md p-3 shadow-lg pointer-events-auto">
            <div className="label-cap text-slate-500 mb-2">Vehicle Status</div>
            {Object.entries(STATUS_COLOR).map(([s, c]) => (
              <div key={s} className="flex items-center justify-between gap-6 text-xs py-0.5">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:c}}/>{s}</div>
                <span className="tabular-nums font-semibold">{counts[s]||0}</span>
              </div>
            ))}
            <div className="border-t border-slate-100 mt-2 pt-2 flex items-center justify-between text-xs font-semibold">Total<span>{buses.length}</span></div>
          </div>
        </div>
      </div>

      <aside className="w-full lg:w-[340px] border-t lg:border-t-0 lg:border-l border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200 space-y-2">
          <div className="label-cap text-slate-500">Live Vehicles</div>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search bus / route / driver…"
                 className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>
          <select value={statusF} onChange={e=>setStatusF(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
            {["All", ...Object.keys(STATUS_COLOR)].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-auto scroll-thin p-2 space-y-2">
          {filtered.slice(0, 30).map(b => (
            <button key={b.id} onClick={() => setSelected(b)}
                    className={`w-full text-left border rounded-md p-2.5 transition-colors ${selected?.id===b.id?"border-[#1E3A8A] bg-blue-50":"border-slate-200 hover:bg-slate-50"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bus size={14} weight="duotone" style={{color:STATUS_COLOR[b.status]}}/>
                  <span className="font-mono font-bold text-[#1E3A8A] text-sm">{b.fleet_id}</span>
                </div>
                <span className="text-[10px] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{background:STATUS_COLOR[b.status]}}/>{b.status}</span>
              </div>
              <div className="text-xs text-slate-600 mt-0.5">{b.registration_number} · {fmtDepot(b.depot, depotMap)}</div>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                <span>🔋 {b.soc}%</span>
                <span>{b.speed} km/h</span>
                <span className={b.delay_min>5?"text-[#F97316] font-semibold":""}>{b.delay_min>0?`+${b.delay_min}m`:"On time"}</span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Mobile-only bottom sheet for breakdown bus details (Leaflet popups have broken touch scroll). */}
      <MobileBreakdownSheet bus={mobileBreakdownBus} onClose={()=>setMobileBreakdownBus(null)}/>
    </div>
  );
}
